import React, { useState, useMemo } from 'react';
import { Calendar, Clock, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';

interface CalBookingWidgetProps {
  toolResponse: {
    available_slots?: string[];
    booking_requirements?: string[];
    event_title?: string;
    event_duration_minutes?: number;
    timezone?: string;
    total_slots?: number;
    error?: string;
  };
  onSlotSelect?: (slot: string) => void;
  selectedSlot?: string | null;
  className?: string;
}

export function CalBookingWidget({
  toolResponse,
  onSlotSelect,
  selectedSlot,
  className,
}: CalBookingWidgetProps) {
  const [localSelectedSlot, setLocalSelectedSlot] = useState<string | null>(selectedSlot || null);

  const {
    available_slots = [],
    booking_requirements = [],
    event_title = 'Appointment',
    event_duration_minutes = 30,
    timezone = 'UTC',
    total_slots,
    error,
  } = toolResponse;

  // Format and group slots by date
  const groupedSlots = useMemo(() => {
    const grouped: Record<string, string[]> = {};
    
    if (!Array.isArray(available_slots) || available_slots.length === 0) {
      return grouped;
    }
    
    available_slots.forEach((slot) => {
      if (!slot || typeof slot !== 'string') {
        console.warn(`Invalid slot type: ${typeof slot}`, slot);
        return;
      }
      
      // Neither this nor the formatting below can throw: an unreadable string is an Invalid Date.
      const date = new Date(slot);
      if (isNaN(date.getTime())) {
        console.warn(`Invalid date string: ${slot}`);
        return;
      }

      const dateKey = date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });

      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(slot);
    });

    // Sort slots within each date group chronologically
    Object.keys(grouped).forEach((dateKey) => {
      grouped[dateKey].sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
    });

    return grouped;
  }, [available_slots]);

  // Format time for display
  const formatTime = (slot: string): string =>
    new Date(slot).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });

  const handleSlotClick = (slot: string) => {
    setLocalSelectedSlot(slot);
    onSlotSelect?.(slot);
  };

  // Show error state
  if (error) {
    return (
      <Card className={cn('border-destructive', className)}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-destructive">
            <AlertCircle className="h-5 w-5" />
            Booking Error
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  // Show loading state if no slots yet
  if (available_slots.length === 0 && !error) {
    return (
      <Card className={cn('border-muted', className)}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Checking Availability...
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const slotCount = total_slots ?? available_slots.length;

  return (
    <Card className={cn('border-primary/20 bg-card', className)}>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-primary" />
              {event_title}
            </CardTitle>
            <CardDescription className="mt-1">
              {slotCount} available time slot{slotCount !== 1 ? 's' : ''} • {event_duration_minutes} minutes
            </CardDescription>
          </div>
          {timezone && timezone !== 'UTC' && (
            <Badge variant="outline" className="text-xs">
              {timezone}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Booking Requirements */}
        {booking_requirements.length > 0 && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <span className="font-semibold">Required information:</span>{' '}
              {booking_requirements.join(', ')}
            </AlertDescription>
          </Alert>
        )}

        {/* Available Slots */}
        {Object.keys(groupedSlots).length > 0 ? (
          <div className="space-y-4">
            {Object.entries(groupedSlots).map(([dateKey, slots]) => (
              <div key={dateKey} className="space-y-2">
                <h4 className="text-sm font-semibold text-foreground">{dateKey}</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                  {slots.map((slot) => {
                    const isSelected = localSelectedSlot === slot || selectedSlot === slot;
                    return (
                      <Button
                        key={slot}
                        variant={isSelected ? 'default' : 'outline'}
                        size="sm"
                        className={cn(
                          'h-auto py-2 px-3 flex flex-col items-center gap-1',
                          isSelected && 'bg-primary text-primary-foreground'
                        )}
                        onClick={() => handleSlotClick(slot)}
                      >
                        <Clock className="h-3 w-3" />
                        <span className="text-xs font-medium">{formatTime(slot)}</span>
                        {isSelected && (
                          <CheckCircle2 className="h-3 w-3 mt-0.5" />
                        )}
                      </Button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              No available time slots found for the selected date.
            </AlertDescription>
          </Alert>
        )}

        {/* Selected Slot Info */}
        {localSelectedSlot && (
          <Alert className="bg-primary/5 border-primary/20">
            <CheckCircle2 className="h-4 w-4 text-primary" />
            <AlertDescription>
              <span className="font-semibold">Selected:</span>{' '}
              {new Date(localSelectedSlot).toLocaleString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
              })}
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

